// Leistungen und Artikel (/rechnung/leistungen, server/src/modules/rechnungstool/artikel.ts): Typen und Laden
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { rateAus } from './formular';
import type { Rate } from './types';

export interface Leistung {
  id: string;
  name: string;
  // kommt mit auf die Rechnung („Name – Beschreibung“)
  description: string | null;
  unit: string;
  // Preis netto in Euro, bis 4 Nachkommastellen; null: jedes Mal eintragen
  price: number | null;
  rate: Rate;
  // Lager (nur Billtoro, in der Kanzlei-App immer leer bzw. aus): Artikelnummer, „Bestand führen“, Mindestbestand und
  // Bestand in der Einheit (Bestand nur mit „Bestand führen“)
  artikelnummer?: string | null;
  lager?: boolean;
  mindest?: number | null;
  bestand?: number | null;
}

// Neue oder geänderte Leistung (Preis null: ohne festen Preis). Fehlt ein Lager-Feld, bleibt es beim Ändern, wie es ist.
export interface LeistungBody {
  name: string;
  description: string | null;
  unit: string;
  price: number | null;
  rate: Rate;
  artikelnummer?: string | null;
  lager?: boolean;
  mindest?: number | null;
}

export const artikelUrl = (firmId: string) => `/api/rechnungstool/${firmId}/artikel`;
export const einArtikelUrl = (id: string) => `/api/rechnungstool/artikel/${id}`;

// Text der Position aus einer Leistung (höchstens 300 Zeichen, das prüft schon der Server beim Speichern der Leistung)
export const leistungText = (l: Pick<Leistung, 'name' | 'description'>) => (l.description ? `${l.name} – ${l.description}` : l.name);

// Text einer neu übernommenen Position: mit Artikelnummer vorn („SM6 Schraube M6“), höchstens 300 Zeichen wie auf dem
// Server; so steht die Nummer auf Lieferschein, Rechnung und E-Rechnung
export const positionsText = (l: Pick<Leistung, 'name' | 'description' | 'artikelnummer'>) =>
  (l.artikelnummer ? `${l.artikelnummer} ${leistungText(l)}` : leistungText(l)).slice(0, 300);

// gleicher Name (ohne Groß- und Kleinschreibung), auch als Text einer übernommenen Position (mit Artikelnummer)
export const gleicheLeistung = (list: Leistung[], name: string) => {
  const n = name.trim().toLowerCase();
  return list.find((l) => [l.name, leistungText(l), positionsText(l)].some((t) => t.trim().toLowerCase() === n));
};

// Steuer einer Leistung wie im Formular (alte Leistungen mit „13b“: 0 %)
export const leistungAus = (l: Leistung): Leistung => ({ ...l, rate: rateAus(l.rate) ?? 19 });

// Leistungen der Firma laden (für Vorschläge beim Tippen); add: neue Leistung speichern und gleich in der Liste
export function useLeistungen(firmId: string) {
  const [list, setList] = useState<Leistung[]>([]);
  const [loaded, setLoaded] = useState(false);
  const reload = useCallback(async () => {
    try {
      setList((await api.get<{ artikel: Leistung[] }>(artikelUrl(firmId))).artikel.map(leistungAus));
    } catch {
      // ohne Leistungen geht das Formular trotzdem
    } finally {
      setLoaded(true);
    }
  }, [firmId]);
  useEffect(() => {
    void reload();
  }, [reload]);
  const add = useCallback(
    async (b: LeistungBody) => {
      const l = leistungAus(await api.post<Leistung>(artikelUrl(firmId), b));
      setList((cur) => [...cur, l].sort((a, x) => a.name.localeCompare(x.name, 'de')));
      return l;
    },
    [firmId],
  );
  return { list, loaded, reload, add, setList };
}
export type Leistungen = ReturnType<typeof useLeistungen>;
